import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import { GenerateWorkshopEnrollmentNumber } from '../../components/GenerateThink5whysCode';

import '../../styles/Concerns.css';

const TrainingEnrollmentForm = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const location = useLocation();
  const { eventCode, eventId, eventTitle, isFree, fee, feeDetails, userCode, userName } = location.state || {};
  // console.log("code: ",eventCode, " id: ",eventId, " title: ",eventTitle, " isfree; ",isFree, " fee: ",fee, " details: ",feeDetails, " usercode: ",userCode, " username: ",userName);

  const [autoEnrollmentNumber] = useState(GenerateWorkshopEnrollmentNumber());
  const [formErrors, setFormErrors] = useState({});

  const [walletBalanceAmount, setWalletBalanceAmount] = useState(0.00);
  const [walletBalancePoints, setWalletBalancePoints] = useState(0);
  const [walletLastUpdated, setWalletLastUpdated] = useState();
  const [walletLastUpdatedRemarks, setWalletLastUpdatedRemarks] = useState('');


  const [formData, setFormData] = useState({
    numberOfMembers: 1,
    enrollmentNumber: autoEnrollmentNumber,
    enrollmentDate: new Date(),
    eventId: eventId,
    eventCode: eventCode,
    title: eventTitle,
    membersName: "[1. Name: "+userName+", Age: ?? Years, Gender: ??, Relation: Self, Meal Preference: Veg/Non-Veg, Meal Portion: Full/Half]",
    enrollmentRemarks: '',
    userId: authState.user._id,
    userCode: userCode,
    userName: userName,
    createdBy: authState.user._id,
    dateCreated: new Date(),
    isFree: isFree,
    amount: fee,
    feeDetails: feeDetails,
    isPaid: false,
    paymentMode: 'Wallet',
    paymentDate: new Date(),
    paymentRemarks: 'Wallet Txn Id: ',
    isActive: true,
    isDeleted: false,
  });

  useEffect(() => {
    const generatedDetails = generateMemberDetails(formData.numberOfMembers);
    setFormData((prevData) => ({
      ...prevData,
      membersName: generatedDetails,
    }));
  }, [formData.numberOfMembers]);

  const generateMemberDetails = (numberOfMembers, membersName = '') => {
    let memberDetails = '';
    
    for (let i = 1; i <= numberOfMembers; i++) {
      memberDetails += `[${i}. Name: Your or Member's Name, Age: ?? Years, Gender: Male/Female/Others, Relation: Self/Other Relation Type/Friend, Meal Preference: Veg/Non-Veg, Meal Portion: Full/Half]\n`;
    }
    return memberDetails.trim(); 
  };
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.numberOfMembers) {
      errors.numberOfMembers = 'Please input number of members';
    } else if (isNaN(data.numberOfMembers) || data.numberOfMembers < 1 || data.numberOfMembers > 6) {
      errors.numberOfMembers = 'Please enter a number between 1 and 6';
    }
    if (!data.membersName) {
      errors.membersName = 'Please input at least 1 members details in the following format [1. Name: Your or Member\'s Name, Age: ?? Years, Gender:  Male/Female/Others, Relation: Self/Other Relation Type/Friend, Meal Preference: Veg/Non-Veg, Meal Portion: Full/Half]';
    }
    if (!data.enrollmentRemarks) {
      errors.enrollmentRemarks = 'Please input remarks';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const config = {
      url: '/events/enroll',
      method: 'post',
      data: { formData, userId: authState.user._id },
      headers: { Authorization: authState.token },
    };

    try {
      await fetchData(config);
      navigate('/event-flow-eventlist/my');
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const fieldError = (field) => (
    <p className={`mt-1 text-pink-600 text-sm ${formErrors[field] ? 'block' : 'hidden'}`}>
      <i className='mr-2 fa-solid fa-circle-exclamation'></i>
      {formErrors[field]}
    </p>
  );


//WalletBalance
const fetchWalletBalance = useCallback(() => {
  const config = {
      url: "/walletbalance",
      method: "get",
      headers: { Authorization: authState.token }
  };

  fetchData(config, { showSuccessToast: false })
      .then(response => {
          if (response) {
              // console.log("Wallet Balance Response in array:", response);
              setWalletBalanceAmount(response.balanceAmount);
              setWalletBalancePoints(response.balancePoints);
              setWalletLastUpdated(response.lastTransactionDate);
              setWalletLastUpdatedRemarks(response.remarks);
          } else {
              console.error("Invalid response format: Wallet not found in response");
              setWalletBalanceAmount(0);
              setWalletBalancePoints(0);
              setWalletLastUpdated(null);
              setWalletLastUpdatedRemarks('');
          }
      })
      .catch(error => {
          console.error("Error fetching wallet master:", error);
          setWalletBalanceAmount(0);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchWalletBalance();
  }, [authState.isLoggedIn, fetchWalletBalance]);

  const handleRechargeWallet = () => {
    navigate("/wallet-topup");
};

  return (
    <MainLayout>
      <div>
        <h3 className='text-lg mx-2 border-b border-b-gray-300 text-white'>Registration Form</h3>
      </div>
      <div className='m-auto my-2 p-2 border-2 shadow-md rounded-md md:w-full'>
        {loading ? (
          <Loader />
        ) : (
          <div className='text-sm'>
            <div className='flex flex-col justify-center text-white mt-4 ml-2 mb-4'>
              <h2 className='mb-4 text-xl text-yellow-200'>Registration / Enrollment Form For </h2>
              <p> Event Title: { eventTitle } </p>
              <p> User Code: { userCode } </p>
              <p> User Name: { userName } </p>
              {(isFree === true) ? (
                <p className='text-lime-500'>Event has Free Entries</p>
              ): (
                <p>Fee: ₹ { fee.toFixed(2) } ({feeDetails})</p>
              )}
            </div>
            <div className='border-container'>
              <p className='text-yellow-300'>Registration Number: {autoEnrollmentNumber}</p>
            </div>
            {isFree === false && (
              <div className="w-full mx-auto text-black bg-slate-400 rounded-lg shadow-lg p-4 mb-2">
                <div className="mb-2">
                    <div className="mb-4 text-lg text-white">
                        <p>Price to pay: <span className='text-yellow-300'>₹{fee.toFixed(2)}</span></p>
                        <p>Think5Whys Wallet Balance: <span className='text-yellow-300'>₹{walletBalanceAmount.toFixed(2)}</span></p>
                        {fee > walletBalanceAmount ? (
                            <p className="text-red-500">Insufficient Balance by: ₹{(fee - walletBalanceAmount).toFixed(2)}</p>
                        ) : (
                            <p className="text-white">
                                After payment your Wallet balance will be: <span className='text-yellow-300'>₹{(walletBalanceAmount - fee).toFixed(2)}</span>
                            </p>
                        )}
                    </div>
                </div>
                {walletBalanceAmount < fee && (
                    <div className='mb-4 flex text-wrap justify-center'>
                        <button 
                          type="button" 
                          className="block w-full md:w-auto bg-blue-800 text-white py-2 px-4 rounded-md font-medium hover:bg-primary-dark" 
                          onClick={handleRechargeWallet}
                        >
                            Recharge Wallet
                        </button>
                    </div>
                )}
              </div>
            )}
            <div className='text-sm flex flex-col'>
              <label htmlFor='numberOfMembers'>Number of Members:</label>
              <input
                type="number"
                name='numberOfMembers'
                id='numberOfMembers'
                value={formData.numberOfMembers}
                onChange={handleChange}
                placeholder="1"
                min="1"
                max="6"
                step="1"
                style={{ width: '100px' }}
                className="block px-4 py-2 mb-4 border rounded-md text-gray-800 bg-white"
              />
              {fieldError('numberOfMembers')}
            </div>

            <div>
              <label htmlFor='membersName'>Members Details:</label>
              <textarea
                name='membersName'
                id='membersName'
                value={formData.membersName} 
                onChange={handleChange} 
                placeholder={`1. Name: Your or Member's Name, Age: ?? Years, Gender: Male/Female/Others, Relation: Self/Other Relation Type/Friend, Meal Preference: Veg/Non-Veg, Meal Portion: Full/Half`}
                className="px-4 py-2 border rounded-md text-gray-800 bg-white"
                style={{height: '250px', width: '100%'}}
              />
              {fieldError('membersName')}
            </div>

            <div className='mb-2 text-sm flex flex-col'>
              <label htmlFor='enrollmentRemarks'>Remarks:</label>
              <textarea
                name='enrollmentRemarks'
                id='enrollmentRemarks'
                value={formData.enrollmentRemarks}
                placeholder='Input remarks...'
                onChange={handleChange}
                maxLength='500'
                style={{
                  border: '1px solid #ccc',
                  padding: '2px',
                  backgroundColor: '#f0f0f0',
                  color: '#000000',
                  maxWidth: '100%',
                  maxHeight: '150px',
                }}
              />
              {fieldError('enrollmentRemarks')}
            </div>

            <div className='border-container flex flex-col justify-center'>
              <div className='flex justify-center'>
                {Object.keys(formErrors).length > 0 && (
                  <div className='form-errors text-red-300 justify-center items-center'>
                    {Object.keys(formErrors).map((key) => (
                      <div className='flex justify-center items-center' key={key}>
                        {formErrors[key]}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!isFree && walletBalanceAmount >= fee && (
                <div className="flex flex-col p-6 border-2 border-yellow-400 rounded-lg shadow-lg bg-white max-w-md mx-auto my-4">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">Event Enrollment / Registration Summary</h2>
                    <div className="space-y-2 text-gray-700">
                        <p><strong>Registration Number:</strong> {autoEnrollmentNumber}</p>
                        <p><strong>Registration Date:</strong> {formData.enrollmentDate.toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', timeZoneName: 'short' })}</p>
                        <p><strong>Registrant Name:</strong> {userName}</p>
                        <p><strong>Address:</strong> Online</p>
                        <p><strong>Particulars:</strong> Event Registration</p>
                        <p className="text-lg font-bold text-green-600"><strong>To Pay:</strong> ₹{fee}</p>
                        <p className="text-lg font-bold text-green-600"><strong>Payment Amount:</strong> ₹{fee}</p>
                        <p><strong>Payment Date:</strong> {formData.enrollmentDate.toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', timeZoneName: 'short' })}</p>
                    </div>
                    {/* <div className="flex justify-center mt-6">
                    <button
                        type="button"
                        className="bg-green-800 text-white py-2 px-6 rounded-md font-medium hover:bg-green-900 transition duration-300 ease-in-out"
                        onClick={() => walletPaymentClicked(autoEnrollmentNumber, formData.enrollmentDate, userName, eventTitle, fee)}
                    >
                        Confirm Now To Register. Your Think5Whys Wallet Will Be Charged.
                    </button>
                    </div> */}
                </div>
              )}
              <div className='flex justify-center'>
                <button
                  className='bg-primary text-white px-4 py-2 font-medium hover:bg-primary-dark'
                  onClick={handleSubmit}
                >
                  Submit
                </button>
                <button
                  className='ml-4 bg-red-500 text-white px-4 py-2 font-medium'
                  onClick={() => navigate('/event-flow-eventlist/open')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TrainingEnrollmentForm;
